import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SubscriptionRevenueReport } from "@/components/reports/SubscriptionRevenueReport";
import { CategoryPopularityReport } from "@/components/reports/CategoryPopularityReport";
import { TailorProductivityReport } from "@/components/reports/TailorProductivityReport";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Calendar } from "lucide-react";
import { useReportsData } from "@/hooks/reports";

const Reports = () => {
  const { mockData, timeRange } = useReportsData();

  // Format the current time range for display
  const formatTimeRange = () => {
    const now = new Date();
    let startDate;
    
    switch (timeRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        return `Last 7 days (${startDate.toLocaleDateString()} - ${now.toLocaleDateString()})`;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        return `Last month (${startDate.toLocaleDateString()} - ${now.toLocaleDateString()})`;
      case 'quarter':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3);
        return `Last quarter (${startDate.toLocaleDateString()} - ${now.toLocaleDateString()})`;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        return `Last year (${startDate.toLocaleDateString()} - ${now.toLocaleDateString()})`;
      default:
        return '';
    }
  };

  return (
    <div className="space-y-8">
      <Card className="glass-card p-6 animate-fadeIn">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-semibold mb-2">Reports & Analytics</h2>
            <p className="text-muted-foreground">
              View detailed reports on subscriptions, products, and tailor productivity.
            </p>
          </div>
          <div className="bg-muted/40 px-4 py-2 rounded-md flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{formatTimeRange()}</span>
          </div>
        </div>
      </Card>

      {mockData && (
        <Alert variant="warning" className="bg-amber-50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Using demonstration data</AlertTitle>
          <AlertDescription>
            We're currently showing mock data for demonstration purposes due to a database timeout.
            Some reports may not reflect actual business data.
          </AlertDescription>
        </Alert>
      )}

      <Card className="glass-card">
        <Tabs defaultValue="subscription-revenue" className="w-full">
          <CardHeader>
            <TabsList className="grid grid-cols-3 gap-2">
              <TabsTrigger value="subscription-revenue">Subscription Revenue</TabsTrigger>
              <TabsTrigger value="category-popularity">Products</TabsTrigger>
              <TabsTrigger value="tailor-productivity">Tailor Productivity</TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent className="pt-2 pb-6">
            <TabsContent value="subscription-revenue" className="mt-0">
              <SubscriptionRevenueReport />
            </TabsContent>
            <TabsContent value="category-popularity" className="mt-0">
              <CategoryPopularityReport />
            </TabsContent>
            <TabsContent value="tailor-productivity" className="mt-0">
              <TailorProductivityReport />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Reports;
